import {
  isSentinel,
  sentinelLabel,
  formatCurrency,
  formatPercent,
  enumLabel,
  formatAgeReductionSchedule,
  formatRateGuarantee,
  formatPreExistingConditionMonthsDetail,
  formatMultipleDollarAmounts,
  formatEligibilityHours,
} from "./shared";

const NOT_INCLUDED = sentinelLabel("notIncluded");
const NOT_OFFERED = sentinelLabel("notOffered");

/** A single flat dollar benefit renders as "Flat $X" */
const flatCurrency = (value: unknown): string => {
  const amount = formatCurrency(value);
  return amount ? `Flat ${amount}` : "";
};

export type CriticalIllnessFieldFormatter = (
  row: Record<string, unknown>,
) => string;

/**
 * Employee/Spouse benefit description — increments, percentage, dollarAmount,
 * multipleDollarAmounts. Minimum and amount are stashed siblings.
 */
const benefitDescription =
  (typeKey: string, amountKey: string): CriticalIllnessFieldFormatter =>
  (row) => {
    const type = row[typeKey];
    if (isSentinel(type)) return sentinelLabel(type as string);
    if (type === "dollarAmount") return flatCurrency(row[amountKey]);
    if (type === "percentage" || type === "percentageOfEeBenefit")
      return formatPercent(row[amountKey]) || "";
    if (type === "increments") {
      return row[amountKey]
        ? `${formatCurrency(row[amountKey])} increments`
        : "";
    }
    if (type === "flatAmount")
      return formatMultipleDollarAmounts(row[amountKey]);
    if (type === "multipleDollarAmounts")
      return formatMultipleDollarAmounts(row[amountKey]);
    return "";
  };

/**
 * Employee/Spouse benefit maximum — dollarAmount, percentageOfEeBenefit,
 * percentageOfEeBenefitAndDollarAmount. Dollar and percent are stashed siblings.
 */
const benefitMaximum =
  (
    typeKey: string,
    dollarKey: string,
    percentKey: string,
  ): CriticalIllnessFieldFormatter =>
  (row) => {
    const type = row[typeKey];
    if (isSentinel(type)) return sentinelLabel(type as string);
    if (type === "dollarAmount") return flatCurrency(row[dollarKey]);
    if (type === "percentageOfEeBenefit")
      return formatPercent(row[percentKey]) || "";
    if (type === "percentageOfEeBenefitAndDollarAmount") {
      const pct = formatPercent(row[percentKey]);
      const cap = formatCurrency(row[dollarKey]);
      if (pct && cap) return `${pct} up to ${cap}`;
      return pct || cap || "";
    }
    return "";
  };

/**
 * Guarantee issue — percentageOfEeBenefit → X%, flatAmount → Flat $X,
 * notIncluded → "Not Included".
 */
const guaranteeIssue =
  (typeKey: string, amountKey: string): CriticalIllnessFieldFormatter =>
  (row) => {
    const type = row[typeKey];
    if (type === "notIncluded") return NOT_INCLUDED;
    if (isSentinel(type)) return sentinelLabel(type as string);
    if (type === "percentageOfEeBenefit")
      return formatPercent(row[amountKey]) || "";
    if (type === "flatAmount") return flatCurrency(row[amountKey]);
    return "";
  };

/**
 * Illness coverage (invasiveCancer, nonInvasiveCancer, etc.) —
 * percentage → X%, notIncluded → "Not Included".
 */
const illnessCoverage =
  (typeKey: string, amountKey: string): CriticalIllnessFieldFormatter =>
  (row) => {
    const type = row[typeKey];
    if (type === "notIncluded") return NOT_INCLUDED;
    if (isSentinel(type)) return sentinelLabel(type as string);
    if (type === "percentage") return formatPercent(row[amountKey]) || "";
    return "";
  };

/**
 * Condition rider (Skin Cancer, Advanced Parkinson's, ALS) — percentage → X%,
 * dollarAmount → $X, sentinels pass through (CD-2325).
 */
const formatConditionRider =
  (typeKey: string, amountKey: string): CriticalIllnessFieldFormatter =>
  (row) => {
    const type = row[typeKey];
    if (isSentinel(type)) return sentinelLabel(type as string);
    if (type === "percentage") return formatPercent(row[amountKey]) || "";
    if (type === "dollarAmount") return formatCurrency(row[amountKey]) || "";
    return "";
  };

const MAX_PAYOUT_LABELS: Record<string, string> = {
  noLimit: "No Limit",
  unlimited: "Unlimited",
  willNotPayForThirdOrLaterOccurrence: "Will not pay for 3rd+ occurrence",
};

const formatMaximumPayout: CriticalIllnessFieldFormatter = (row) => {
  const type = row["maximumPayoutType"];
  // Unlike other sentinels, a notIncluded maximum payout displays as
  // "Not Disclosed": product decision in CD-3148.
  if (type === "notIncluded") return sentinelLabel("notDisclosed");
  if (isSentinel(type)) return sentinelLabel(type as string);
  if (type === "dollarAmount") return flatCurrency(row["maximumPayoutAmount"]);
  if (type === "percentage")
    return formatPercent(row["maximumPayoutAmount"]) || "";
  if (typeof type === "string" && type in MAX_PAYOUT_LABELS)
    return MAX_PAYOUT_LABELS[type];
  return "";
};

const formatOccurrence =
  (typeKey: string, monthsKey: string): CriticalIllnessFieldFormatter =>
  (row) => {
    const type = row[typeKey];
    if (isSentinel(type)) return sentinelLabel(type as string);
    if (type === "included") return "Included";
    if (type === "notIncluded") return NOT_INCLUDED;
    if (type === "months") {
      const months = row[monthsKey];
      return months != null ? `${String(months)} months` : "";
    }
    return "";
  };

/** Pre-existing condition — same pattern as cancer and hospital. */
const formatPreExistingCondition: CriticalIllnessFieldFormatter = (row) => {
  const type = row["preExistingConditionType"];
  if (isSentinel(type)) return sentinelLabel(type as string);
  if (type === "waived") return "Waived";
  if (type === "none") return "None";
  if (type !== "months") return "";
  const detail = row["preExistingConditionMonthsDetail"];
  return typeof detail === "string" && detail
    ? formatPreExistingConditionMonthsDetail(detail)
    : "";
};

const AGE_BASIS_LABELS: Record<string, string> = {
  issueAge: "Issue Age",
  attainedAge: "Attained Age",
  notIncluded: NOT_INCLUDED,
};

const ANNUAL_OPEN_ENROLLMENT_LABELS: Record<string, string> = {
  oneTimeOpenEnrollment: "One Time Open Enrollment",
  annualOpenEnrollment: "Annual Open Enrollment",
  modifiedOpenEnrollment: "Modified Open Enrollment",
  included: "Included",
  notIncluded: NOT_INCLUDED,
  notOffered: NOT_OFFERED,
};

export const criticalIllnessFormatters: Record<
  string,
  CriticalIllnessFieldFormatter
> = {
  // Employee
  employeeBenefitDescription: benefitDescription(
    "employeeBenefitDescriptionType",
    "employeeBenefitDescriptionAmount",
  ),
  employeeBenefitMaximum: benefitMaximum(
    "employeeBenefitMaximumType",
    "employeeBenefitMaximumDollarAmount",
    "employeeBenefitMaximumPercent",
  ),
  employeeGuaranteeIssue: guaranteeIssue(
    "employeeGuaranteeIssueType",
    "employeeGuaranteeIssueAmount",
  ),

  // Spouse
  spouseBenefitDescription: benefitDescription(
    "spouseBenefitDescriptionType",
    "spouseBenefitDescriptionAmount",
  ),
  spouseBenefitMaximum: benefitMaximum(
    "spouseBenefitMaximumType",
    "spouseBenefitMaximumDollarAmount",
    "spouseBenefitMaximumPercent",
  ),
  spouseGuaranteeIssue: guaranteeIssue(
    "spouseGuaranteeIssueType",
    "spouseGuaranteeIssueAmount",
  ),

  // Child
  childBenefitDescription: benefitDescription(
    "childBenefitDescriptionType",
    "childBenefitDescriptionAmount",
  ),

  // Illness coverage
  invasiveCancer: illnessCoverage("invasiveCancerType", "invasiveCancerAmount"),
  nonInvasiveCancer: illnessCoverage(
    "nonInvasiveCancerType",
    "nonInvasiveCancerAmount",
  ),
  category1HeartAttack: illnessCoverage(
    "category1HeartAttackType",
    "category1HeartAttackAmount",
  ),
  category1Stroke: illnessCoverage(
    "category1StrokeType",
    "category1StrokeAmount",
  ),
  kidneyFailure: illnessCoverage("kidneyFailureType", "kidneyFailureAmount"),

  // Payout and occurrences
  maximumPayout: formatMaximumPayout,
  occurrenceOfDifferentIllness: formatOccurrence(
    "occurrenceOfDifferentIllnessType",
    "occurrenceOfDifferentIllnessMonths",
  ),
  additionalOccurrenceOfSameIllness: formatOccurrence(
    "additionalOccurrenceOfSameIllnessType",
    "additionalOccurrenceOfSameIllnessMonths",
  ),

  ageReductionSchedule: formatAgeReductionSchedule,

  // Pre-existing condition
  preExistingCondition: formatPreExistingCondition,

  // Wellness
  wellnessBenefit: (row) => {
    const type = row["wellnessBenefitType"];
    if (type === "notIncluded") return NOT_INCLUDED;
    if (isSentinel(type)) return sentinelLabel(type as string);
    if (type === "dollarAmount")
      return formatCurrency(row["wellnessBenefitAmount"]) || "";
    return "";
  },

  // Simple enums
  portability: enumLabel("portability", {
    included: "Included",
    notIncluded: NOT_INCLUDED,
    notOffered: NOT_OFFERED,
  }),
  ageBasis: enumLabel("ageBasis", AGE_BASIS_LABELS),
  annualOpenEnrollment: enumLabel(
    "annualOpenEnrollment",
    ANNUAL_OPEN_ENROLLMENT_LABELS,
  ),
  rateGuarantee: formatRateGuarantee,

  // Extended (non-v5) fields — type+amount pairs (CD-2325). The MP export
  // drops any row whose formatter returns undefined (`fieldValues` in
  // marketing-presentation-proposal.ts), so an entry is what puts these rows
  // on the sheet at all.
  skinCancer: formatConditionRider("skinCancerType", "skinCancerAmount"),
  advancedParkinsons: formatConditionRider(
    "advancedParkinsonsType",
    "advancedParkinsonsAmount",
  ),
  als: formatConditionRider("alsType", "alsAmount"),
  eligibilityHours: formatEligibilityHours,
};

export const formatCriticalIllnessField = (
  fieldId: string,
  row: Record<string, unknown>,
): string | undefined => {
  const formatter = criticalIllnessFormatters[fieldId];
  return formatter ? formatter(row) : undefined;
};
